import { cUtil }                   from "../../functions/util";
import { cAdminDeliveryItems }     from "./admin-delivery-items";
import { cAdminDeliveryItemsItem } from "./admin-delivery-items-item";
import { cAdminDeliveryItemsExtra } from "./admin-delivery-items-extra";

interface CouponShare {
  coupon : cAdminDeliveryItemsExtra;
  base   : number;
  share  : number;
  floor  : number;
  rest   : number;
}

export class cAdminDeliveryItemsOrder {

  div            = document.createElement('div');
  div_head       = document.createElement('div');
  h3_label       = document.createElement('h3');
  a_order        = document.createElement('a');
  div_sum        = document.createElement('div');
  span_ordered   = document.createElement('span');
  strong_ordered = document.createElement('strong');
  span_sum       = document.createElement('span');
  strong_sum     = document.createElement('strong');
  button_capture = document.createElement('button');
  p_captureHint  = document.createElement('p');
  div_problem    = document.createElement('div');
  p_problem      = document.createElement('p');
  button_ack     = document.createElement('button');
  table          = document.createElement('table');
  thead          = document.createElement('thead');
  tbody          = document.createElement('tbody');

  data   : DeliveryOrderGroup;
  items  : cAdminDeliveryItemsItem[]  = [];
  extras : cAdminDeliveryItemsExtra[] = [];

  constructor(parent: HTMLElement, data: DeliveryOrderGroup, page: cAdminDeliveryItems, customerLabel: string) {

    this.data = data;

    // ---- Structure ----
    parent.append(this.div);
      this.div.append(this.div_head);
        this.div_head.append(this.h3_label);
        this.div_head.append(this.div_sum);
          this.div_sum.append(this.span_ordered);
            this.span_ordered.append(this.strong_ordered);
          this.div_sum.append(this.span_sum);
            this.span_sum.append(this.strong_sum);
          // Only an order paid through wallee is captured here. The section without a woocommerce
          // order has nothing to capture against, an invoice order no transaction.
          if (this.canCapture()) this.div_sum.append(this.button_capture);
      if (this.canCapture()) this.div.append(this.p_captureHint);
      if (data.problem !== '') this.div.append(this.div_problem);
        this.div_problem.append(this.p_problem);
        this.div_problem.append(this.button_ack);
      this.div.append(this.table);
        this.table.append(this.thead);
        this.table.append(this.tbody);

    // ---- Init ----
    this.div         .classList.add('c-order');
    this.div_head    .classList.add('c-order-head');
    this.div_sum     .classList.add('c-order-sum');
    this.div_problem .classList.add('c-order-problem');
    this.p_captureHint.classList.add('c-capture-hint');

    // The target of the links in the notice above the list.
    if (!data.isLoose) this.div.id = `c-order-${data.order_id}`;

    // Closing the problem leaves the steps in woocommerce to the person: set the order to
    // Abgeschlossen if it is not already. The note on the order is written by the server.
    this.p_problem.innerText  = data.problem;
    this.button_ack.type      = 'button';
    this.button_ack.innerText = 'Erledigt';
    this.button_ack.title     = 'Schreibt eine Bestellnotiz und blendet das Problem aus';
    this.button_ack.classList.add('button');

    this.label(customerLabel);

    this.span_ordered.prepend('Bestellsumme');
    this.span_sum.prepend('Liefersumme');
    this.button_capture.type      = 'button';
    this.button_capture.innerText = 'Capture';

    this.header();

    // The server sends the lines sorted by delivery date. An order is captured once, after its
    // last delivery, so the sums cover all of its lines, whatever day they are delivered on.
    for (const item of data.items) {
      this.items.push(new cAdminDeliveryItemsItem(this.tbody, item, page, this));
    }

    // Coupons and the reserve below the lines, in that order.
    for (const extra of data.extras) {
      this.extras.push(new cAdminDeliveryItemsExtra(this.tbody, extra, page, this));
    }

    // What the customer ordered. Fixed, so it is computed once.
    this.strong_ordered.innerText = this.orderedSum();

    this.updateSum();

    // Captures the Liefersumme at wallee (capture.php). Asked once more, it cannot be undone. On
    // success the page stays: the order is locked as the server now treats it, and the message
    // under the heading tells to reload for the state wallee reports.
    this.button_capture.addEventListener('click', async () => {
      const amount = this.strong_sum.innerText;
      if (!window.confirm(`CHF ${amount} bei wallee einziehen? Das lässt sich nicht rückgängig machen.`)) return;

      this.button_capture.disabled  = true;
      this.button_capture.innerText = 'Wird eingezogen …';
      const result = await page.fetchCapture(data.order_id, amount);
      this.button_capture.innerText = 'Capture';

      if (result.ok) {
        const message = page.reloadHint(result.message);
        page.toast(message, false);
        this.lock(message);
        return;
      }
      page.toast(result.message, true);
      this.updateSum();
      this.showCaptureHint(result.message);
    });

    // The page stays. The notice above the list still counts the order until it is reloaded.
    this.button_ack.addEventListener('click', async () => {
      this.button_ack.disabled = true;
      const result = await page.fetchProblemAck(data.order_id, data.problemJobId);
      if (!result.ok) {
        this.button_ack.disabled = false;
        page.toast(result.message, true);
        return;
      }
      const message = page.reloadHint(result.message);
      page.toast(message, false);
      this.p_problem.innerText = message;
    });
  }

  // After a capture: the rows can no longer be changed and the button stays locked, with the
  // message as its reason.
  lock(message: string): void {
    this.data.captured     = true;
    this.data.captureBlock = message;
    for (const item  of this.items)  item.lock();
    for (const extra of this.extras) extra.lock();
    this.updateSum();
  }

  // A deleted line leaves the page at once. A section left without rows goes with it, and a
  // customer left without sections as well.
  removeItem(item: cAdminDeliveryItemsItem): void {
    item.tr.remove();
    this.items = this.items.filter((other) => other !== item);
    this.updateSum();
    if (this.items.length > 0 || this.extras.length > 0) return;

    const customerDiv = this.div.parentElement;
    this.div.remove();
    if (customerDiv !== null && customerDiv.querySelector('.c-order') === null) customerDiv.remove();
  }

  // Kunde · #11371 · 24.09.2026 · Zahlung ausstehend. The order number links to the order in
  // woocommerce. The section without an order has neither link, date nor status.
  label(customerLabel: string): void {
    const parts: (string | HTMLElement)[] = [customerLabel];

    if (this.data.isLoose) {
      parts.push(this.data.orderLabel);
    }
    else {
      const urlParams = new URLSearchParams();
      urlParams.set('page'  , 'wc-orders');
      urlParams.set('action', 'edit');
      urlParams.set('id'    , String(this.data.order_id));
      this.a_order.href      = '/wp-admin/admin.php?' + urlParams.toString();
      this.a_order.innerText = this.data.orderLabel;
      parts.push(this.a_order);
    }

    if (this.data.orderDate   !== '') parts.push(cUtil.isoToCh(this.data.orderDate));
    if (this.data.orderStatus !== '') parts.push(this.data.orderStatus);

    parts.forEach((part, i) => {
      if (i > 0) this.h3_label.append(' · ');
      this.h3_label.append(part);
    });
  }

  header(): void {
    // No column for the order: its number stands in the heading above the table.
    const tr      = document.createElement('tr');
    const columns = [
      '', 'Liefertermin', 'Produkt', 'Bestellt',
      'Geliefert', 'Preis', 'Steuer / Total', 'Lieferschein',
    ];
    for (const column of columns) {
      const th = document.createElement('th');
      th.innerText = column;
      tr.append(th);
    }
    this.thead.append(tr);
  }

  // The total of the woocommerce order, with tax, coupons and the reserve: what was authorized at
  // checkout, to the Rappen. It is not added up from the lines, which round on 5 Rappen and would
  // miss it by a Rappen or two. The Liefersumme keeps rounding on 5 Rappen.
  orderedSum(): string {
    return this.data.orderTotal === null ? '–' : this.data.orderTotal.toFixed(2);
  }

  // A line changed its delivered value, so the coupon is spread anew over its tax rates, in
  // proportion to what is delivered at each rate - the way the coupon type spreads it at checkout
  // (allocate() in objects/coupon/coupon.php). What is spread is the coupon as ordered: an amount
  // entered by hand on a coupon row gives way to it. A line without a valid delivered value yet
  // counts with what was ordered, so the rows stay sensible while the weights are being entered.
  redistributeCoupons(): void {
    const shares: CouponShare[] = this.extras
      .filter((extra) => extra.isCoupon)
      .map((coupon) => ({coupon, base: 0, share: 0, floor: 0, rest: 0}));
    if (shares.length === 0) return;

    for (const item of this.items) {
      const entry = shares.find((s) => s.coupon.data.tax_rate === item.data.tax_rate);
      if (entry) entry.base += Math.round((item.subTotal() ?? item.orderedSubTotal()) * 100);
    }

    const amount = Math.round(-shares.reduce((sum, s) => sum + s.coupon.data.ordered_net, 0) * 100);
    this.allocate(amount, shares);

    for (const s of shares) s.coupon.setDelivered(-s.share / 100);
  }

  // Verfahren der grössten Reste, in Rappen, like allocate() in objects/coupon/coupon.php: every
  // rate gets its share rounded down, the Rappen left go to the largest remainders, on a tie to the
  // larger base. No rate gets more than its base, what it cannot take goes to the others in the
  // next round.
  allocate(amount: number, shares: CouponShare[]): void {
    amount = Math.min(amount, shares.reduce((sum, s) => sum + s.base, 0));

    while (amount > 0) {
      const open  = shares.filter((s) => s.base > 0 && s.share < s.base);
      const total = open.reduce((sum, s) => sum + s.base, 0);
      if (total === 0) break;

      for (const s of open) {
        s.floor = Math.floor(amount * s.base / total);
        s.rest  = (amount * s.base) % total;
      }
      open.sort((a, b) => b.rest - a.rest || b.base - a.base);

      const left = amount - open.reduce((sum, s) => sum + s.floor, 0);
      open.forEach((s, i) => {
        const share = Math.min(s.floor + (i < left ? 1 : 0), s.base - s.share);
        s.share += share;
        amount  -= share;
      });
    }
  }

  // What is captured: the Total of every row, the lines as delivered, the coupons and the reserve
  // with what is entered under Geliefert. Follows every keystroke. The rows bring their values
  // along already rounded, nothing is rounded on the order any more.
  updateSum(): void {
    let sum      = 0;
    let complete = true;

    const totals = [
      ...this.items .map((item)  => item.lineTotal()),
      ...this.extras.map((extra) => extra.total()),
    ];

    for (const total of totals) {
      if (total === null) {
        complete = false;
        continue;
      }
      sum += total;
    }

    this.strong_sum.innerText = complete ? sum.toFixed(2) : '–';
    this.updateCapture(Math.round(sum * 100) / 100, complete);
  }

  canCapture(): boolean {
    return !this.data.isLoose && this.data.authorizationAmount !== null;
  }

  // The button is locked as long as anything speaks against the capture, and says why
  // (spec/wallee-restaufrufe.md, 6.3). The server checks all of it again.
  updateCapture(sum: number, complete: boolean): void {
    if (!this.canCapture()) return;

    const authorized = this.data.authorizationAmount ?? 0;
    let reason = this.data.captureBlock;

    if (reason === '' && !complete) {
      reason = 'Noch nicht alle Positionen haben ein Gewicht, einen Preis und einen Liefertermin.';
    }
    if (reason === '') reason = this.couponProblem();
    if (reason === '' && sum <= 0) {
      reason = 'Die Liefersumme ist 0 oder kleiner. Die Bestellung im Plugin stornieren (Void).';
    }
    if (reason === '' && sum > authorized + 0.001) {
      reason =
        `Die Liefersumme liegt ${(sum - authorized).toFixed(2)} über dem autorisierten Betrag ` +
        `${authorized.toFixed(2)}. Mit einem negativen Betrag unter Geliefert der Reserve ausgleichen.`;
    }

    this.button_capture.disabled = reason !== '';
    this.button_capture.title    = reason;
    this.showCaptureHint(reason);
  }

  showCaptureHint(text: string): void {
    this.p_captureHint.innerText     = text;
    this.p_captureHint.style.display = text === '' ? 'none' : '';
  }

  // A coupon is spread over the delivered lines of its tax rate at the capture, so it needs such
  // lines, and it cannot take off more than they are worth.
  couponProblem(): string {
    for (const coupon of this.extras.filter((extra) => extra.isCoupon)) {
      const net = coupon.amounts()?.net ?? 0;
      if (net === 0) continue;

      let base = 0;
      for (const item of this.items) {
        if (item.data.tax_rate === coupon.data.tax_rate) base += item.subTotal() ?? 0;
      }
      const rate = `${coupon.data.tax_rate.toFixed(1)} %`;
      if (base <= 0)          return `Der Gutschein zu ${rate} hat keine gelieferte Position. Bitte auf 0 setzen.`;
      if (-net > base + 0.001) return `Der Gutschein zu ${rate} ist grösser als der gelieferte Warenwert.`;
    }
    return '';
  }
}
